/* eslint-disable */
import React, { useEffect, useRef, useCallback, useState } from "react";
import {
  Box,
  Avatar,
  Group,
  ActionIcon,
  ScrollArea,
  Paper,
  Text,
  TextInput,
  Button,
} from "@mantine/core";
import {
  IconDots,
  IconPaperclip,
  IconSend,
  IconSearch,
} from "@tabler/icons-react";
import { useSendMessage } from "api/chat/sendMessage";
import { useQueryClient } from "@tanstack/react-query";
import { Phone } from "tabler-icons-react";
import type { Socket } from "socket.io-client";
import { wrapByWords } from "utils/lineChangeText";

type Conversation = {
  id: string;
  name: string;
  last: string;
  time: string;
  unread: number;
  status: "online" | "away" | "offline";
  type: "direct" | "group";
  otherUserId?: string;
};

type Message = {
  id: string;
  clientId?: string;
  from: "them" | "me";
  text: string;
  time: string;
  senderName?: string;
};

function extractText(content: any): string {
  if (content == null) return "";
  if (typeof content === "string") return content;
  if (typeof content === "number" || typeof content === "boolean")
    return String(content);
  if (typeof content === "object") {
    if (typeof content.text === "string") return content.text;
    if (typeof content.body === "string") return content.body;
    if (typeof content.message === "string") return content.message;
    if (typeof content.content === "string") return content.content;
    try {
      return JSON.stringify(content);
    } catch {
      return "[unsupported message content]";
    }
  }
  return String(content);
}

export default function MessageInBox({
  activeConversation,
  messages,
  classes = {},
  socket,
  isUserOnline,
  onlineUsers,
  emitTyping,
  emitRead,
  joinConversation,
}: {
  activeConversation?: Conversation | null;
  messages: any[];
  classes?: Record<string, string>;
  socket?: Socket | null;
  emitTyping?: (payload: any) => void;
  emitRead?: (payload: any) => void;
  joinConversation?: (conversationId: string) => void;
  isUserOnline: any;
  onlineUsers: any;
}) {
  const queryClient = useQueryClient();
  const { mutate: sendMessage } = useSendMessage();

  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const typingTimeoutRef = useRef<number | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const pendingClientIdsRef = useRef<Set<string>>(new Set());

  const currentUserId =
    typeof window !== "undefined" ? localStorage.getItem("userId") : null;
  const scrollToBottom = useCallback(
    (smooth = true) => {
      // small timeout to allow DOM to paint (works consistently)
      window.setTimeout(() => {
        try {
          if (endRef.current) {
            endRef.current.scrollIntoView({
              behavior: smooth ? "smooth" : "auto",
              block: "end",
            });
            return;
          }

          // fallback: try to scroll the scroll area container if sentinel not found
          if (classes.messagesWrap) {
            const el = document.querySelector(
              `.${classes.messagesWrap}`
            ) as HTMLElement | null;
            if (el) {
              // Mantine ScrollArea uses a viewport element inside; try to find it
              const viewport = el.querySelector(
                "[data-mantine-scrollarea-viewport], .mantine-ScrollArea-viewport"
              ) as HTMLElement | null;
              const scrollEl = viewport ?? el;
              scrollEl.scrollTop = scrollEl.scrollHeight;
            }
          } else {
            // last-resort: scroll document (unlikely desired)
            window.scrollTo(0, document.body.scrollHeight);
          }
        } catch (e) {
          // ignore scroll errors
        }
      }, 40);
    },
    [classes.messagesWrap]
  );
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      console.log("MessageInBox unmounted");
    };
  }, []);

  const normalizeRaw = useCallback(
    (raw: any): Message => {
      const messageTime =
        raw.time ??
        (raw.createdAt
          ? new Date(raw.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }));

      let from: "them" | "me" = "them";
      if (raw.from === "me" || raw.from === "them") {
        from = raw.from;
      } else if (currentUserId && raw.sender) {
        from = String(raw.sender) === String(currentUserId) ? "me" : "them";
      } else if (activeConversation?.otherUserId && raw.sender) {
        from =
          String(raw.sender) === String(activeConversation.otherUserId)
            ? "them"
            : "me";
      }

      const rawText = raw.text ?? raw.content ?? raw.message ?? raw.body ?? "";
      const t = extractText(rawText);

      return {
        id: String(raw.id ?? raw._id ?? raw.clientId ?? `msg-${Date.now()}`),
        clientId: raw.clientId ?? undefined,
        from,
        text: t,
        time: messageTime,
        senderName: raw.senderName ?? undefined,
      } as Message;
    },
    [currentUserId, activeConversation?.otherUserId]
  );

  useEffect(() => {
    if (!Array.isArray(messages)) {
      setLocalMessages((prev) =>
        prev.filter((m) => String(m.id).startsWith("tmp-"))
      );
      scrollToBottom(false);
      return;
    }

    const serverNormalized = messages.map((m: any) => normalizeRaw(m));

    setLocalMessages((prev) => {
      const serverIdSet = new Set(serverNormalized.map((m) => String(m.id)));
      const serverClientIdSet = new Set(
        serverNormalized.map((m) => (m.clientId ? String(m.clientId) : null))
      );

      const prevTemps = prev.filter((m) => String(m.id).startsWith("tmp-"));
      const remainingTemps = prevTemps.filter(
        (t) =>
          !serverIdSet.has(String(t.id)) &&
          !(t.clientId && serverClientIdSet.has(String(t.clientId)))
      );

      const remainingPending = remainingTemps.filter((t) =>
        pendingClientIdsRef.current.has(String(t.clientId ?? t.id))
      );

      const merged = [...serverNormalized];

      for (const temp of remainingPending) {
        if (
          !merged.some((m) => m.clientId && m.clientId === temp.clientId) &&
          !merged.some((m) => m.id === temp.id)
        ) {
          merged.push(temp);
        }
      }
      return merged;
    });
    scrollToBottom(true);
  }, [messages]);

  useEffect(() => {
    if (!socket) {
      console.debug("No socket instance in MessageInBox");
      return;
    }

    console.debug("[MessageInBox] activeConversation:", {
      id: activeConversation?.id,
      otherUserId: activeConversation?.otherUserId,
    });
    console.debug("[MessageInBox] currentUserId:", currentUserId);

    const handleIncoming = (raw: any) => {
      setTimeout(() => {
        try {
          console.debug("[socket] incoming raw:", raw);
          const convId =
            raw.conversationId ??
            raw.conversation?.id ??
            raw.room ??
            raw.roomId ??
            null;
          const serverMsg = raw.message ?? raw;

          if (
            !activeConversation ||
            String(convId) !== String(activeConversation.id)
          ) {
            console.debug(
              "[socket] message for other conversation (ignoring)",
              {
                convId,
                activeId: activeConversation?.id,
              }
            );
            return;
          }

          const normalized = normalizeRaw(serverMsg);

          setLocalMessages((prev) => {
            if (normalized.clientId) {
              pendingClientIdsRef.current.delete(String(normalized.clientId));
              const idx = prev.findIndex(
                (m) =>
                  m.id === normalized.clientId ||
                  m.clientId === normalized.clientId
              );
              if (idx !== -1) {
                const copy = prev.slice();
                copy[idx] = { ...normalized, id: String(normalized.id) };
                return copy;
              }
            }

            if (prev.some((m) => String(m.id) === String(normalized.id)))
              return prev;
            return [...prev, normalized];
          });

          try {
            emitRead?.({ conversationId: activeConversation.id });
          } catch (e) {}
        } catch (e) {
          console.warn("handleIncoming error", e);
        }
      }, 0);
    };

    const handleTyping = (payload: any) => {
      console.debug("[socket] typing payload:", payload, {
        activeConversationId: activeConversation?.id,
        activeOtherUserId: activeConversation?.otherUserId,
        currentUserId,
      });
      if (!activeConversation) return;

      const convId =
        payload.conversationId ?? payload.room ?? payload.roomId ?? null;
      const sender =
        payload.sender ??
        payload.userId ??
        payload.from ??
        payload.initiator ??
        payload.owner ??
        payload.senderId ??
        null;
      const senderName =
        payload.senderName ?? payload.name ?? payload.userName ?? null;
      const recipient =
        payload.recipientId ?? payload.to ?? payload.recipient ?? null;
      const isTypingFlag =
        typeof payload.isTyping === "boolean" ? payload.isTyping : true;

      let isRelevant = false;
      if (convId && String(convId) === String(activeConversation.id))
        isRelevant = true;
      if (
        !isRelevant &&
        sender &&
        activeConversation.otherUserId &&
        String(sender) === String(activeConversation.otherUserId)
      )
        isRelevant = true;
      if (
        !isRelevant &&
        recipient &&
        currentUserId &&
        String(recipient) === String(currentUserId)
      )
        isRelevant = true;

      if (!isRelevant && activeConversation.otherUserId) {
        const vals = Object.values(payload).map((v: any) =>
          v != null ? String(v) : v
        );
        if (vals.includes(String(activeConversation.otherUserId)))
          isRelevant = true;
      }

      if (!isRelevant) {
        console.debug("[socket] typing event not relevant -> ignored");
        return;
      }

      if (!isTypingFlag) {
        setTyping(false);
        setTypingUser(null);
        if (typingTimeoutRef.current) {
          window.clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
        return;
      }

      setTyping(true);
      setTypingUser(
        senderName ??
          (sender ? String(sender) : activeConversation.name ?? null)
      );

      if (typingTimeoutRef.current)
        window.clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = window.setTimeout(() => {
        setTyping(false);
        setTypingUser(null);
        typingTimeoutRef.current = null;
      }, 1500) as any;
    };

    const handler = ({ unreadCount: count }: { unreadCount: number }) => {};
    socket.on("unreadNotificationsCount", handler);

    socket.on("message", handleIncoming);
    socket.on("new_message", handleIncoming);
    socket.on("conversation:message", handleIncoming);
    socket.on("chat:new_message", handleIncoming);

    socket.on("typing", handleTyping);
    socket.on("user:typing", handleTyping);
    socket.on("chat:typing", handleTyping);

    const onConnect = () =>
      console.debug("[socket] connected", socket.id, socket.connected);
    const onDisconnect = (reason: any) =>
      console.debug("[socket] disconnected", reason);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    return () => {
      socket.off("message", handleIncoming);
      socket.off("new_message", handleIncoming);
      socket.off("conversation:message", handleIncoming);
      socket.off("chat:new_message", handleIncoming);
      socket.off("typing", handleTyping);
      socket.off("user:typing", handleTyping);
      socket.off("chat:typing", handleTyping);
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      if (typingTimeoutRef.current) {
        window.clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [socket, activeConversation, normalizeRaw, emitRead]);
  // useEffect(() => {
  //   // use non-smooth so user doesn't see jump glitch on switching
  //   scrollToBottom(false);
  // }, [activeConversation?.id, scrollToBottom]);

  // // when localMessages change (new message arrived or sent) -> smooth scroll
  // useEffect(() => {
  //   scrollToBottom(true);
  // }, [localMessages.length, scrollToBottom]);
  function getInitials(name?: string) {
    if (!name) return "NA";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  function formatTimeNow() {
    const d = new Date();
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  const handleSend = useCallback(() => {
    try {
      const trimmed = text.trim();
      if (!trimmed) return;

      const conversationId = activeConversation?.id ?? "";
      const recipientId = activeConversation?.otherUserId ?? "";

      if (!conversationId || !recipientId) {
        console.error(
          "Missing conversationId or recipientId.",
          conversationId,
          recipientId
        );
        return;
      }

      const tempId = `tmp-${Date.now()}`;
      const optimisticMessage: Message = {
        id: tempId,
        clientId: tempId,
        from: "me",
        text: trimmed,
        time: formatTimeNow(),
      };

      // setLocalMessages((prev) => {
      //   if (
      //     prev.some(
      //       (m) =>
      //         m.clientId === tempId || (m.text === trimmed && m.from === "me")
      //     )
      //   ) {
      //     return prev;
      //   }
      //   pendingClientIdsRef.current.add(tempId);
      //   return [...prev, optimisticMessage];
      // });

      setText("");

      const payload = {
        conversationId,
        recipientId,
        content: trimmed,
        clientId: tempId,
        parentMessageId: "",
        forwardedMessageId: "",
      };
      scrollToBottom(true);
      const doSocketSend = async () => {
        if (socket && (socket as any).connected) {
          try {
            console.debug("[socket] emit send_message payload:", payload);
            socket.emit("chat:send", payload);
            socket.emit("send_message", payload, (ack: any) => {
              console.debug("[socket] send_message ack:", ack);
              const serverMessage = ack?.data ?? ack;
              const returnedId =
                serverMessage?._id ?? serverMessage?.id ?? null;
              const returnedClientId =
                serverMessage?.clientId ?? payload.clientId;

              if (returnedClientId)
                pendingClientIdsRef.current.delete(String(returnedClientId));

              if (returnedId || returnedClientId) {
                setLocalMessages((prev) =>
                  prev.map((m) =>
                    m.id === tempId || m.clientId === returnedClientId
                      ? {
                          id: returnedId ? String(returnedId) : m.id,
                          clientId: returnedClientId ?? undefined,
                          from: "me",
                          text:
                            serverMessage?.content ??
                            serverMessage?.text ??
                            m.text,
                          time: serverMessage?.createdAt ?? m.time,
                        }
                      : m
                  )
                );
              }

              try {
                queryClient.invalidateQueries(["getMessages", conversationId]);
              } catch {}
            });
            return true;
          } catch (err) {
            console.warn("socket emit failed:", err);
            return false;
          }
        }
        return false;
      };

      const doApiSend = () =>
        new Promise<void>((resolve) => {
          if (!sendMessage) {
            console.warn("sendMessage mutate not available");
            resolve();
            return;
          }
          // sendMessage(
          //   {
          //     conversationId,
          //     recipientId,
          //     content: trimmed,
          //     parentMessageId: "",
          //     forwardedMessageId: "",
          //   },
          //   {
          //     onSuccess: (res) => {
          //       const returnedData =
          //         res && (res as any).data ? (res as any).data : null;
          //       const returnedId =
          //         returnedData?._id ?? returnedData?.id ?? null;
          //       const returnedClientId = returnedData?.clientId ?? tempId;

          //       pendingClientIdsRef.current.delete(String(returnedClientId));

          //       if (returnedId || returnedClientId) {
          //         setLocalMessages((prev) =>
          //           prev.map((m) =>
          //             m.id === tempId || m.clientId === returnedClientId
          //               ? {
          //                   id: returnedId ? String(returnedId) : m.id,
          //                   clientId: returnedClientId ?? undefined,
          //                   from: "me",
          //                   text:
          //                     returnedData?.content ??
          //                     returnedData?.text ??
          //                     m.text,
          //                   time: returnedData?.createdAt ?? m.time,
          //                 }
          //               : m
          //           )
          //         );
          //       }

          //       try {
          //         queryClient.invalidateQueries([
          //           "getMessages",
          //           conversationId,
          //         ]);
          //       } catch {}
          //       resolve();
          //     },
          //     onError: (err) => {
          //       console.error("[api] sendMessage failed:", err);
          //       pendingClientIdsRef.current.delete(tempId);
          //       setLocalMessages((prev) =>
          //         prev.filter((m) => m.id !== tempId && m.clientId !== tempId)
          //       );
          //       resolve();
          //     },
          //   }
          // );
        });

      (async () => {
        const socketOk = await doSocketSend();
        await doApiSend();
        if (!socketOk) {
        }
      })();
    } catch (err) {
      console.error("handleSend unexpected error:", err);
    }
  }, [text, activeConversation, sendMessage, queryClient, socket]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else {
      try {
        if (emitTyping && activeConversation) {
          emitTyping({ conversationId: activeConversation.id });
        }
      } catch (e) {}
    }
  }

  const online =
    Array.isArray(onlineUsers) && activeConversation?.otherUserId
      ? onlineUsers.includes(String(activeConversation?.otherUserId))
      : typeof isUserOnline === "function"
      ? isUserOnline(activeConversation?.otherUserId)
      : false;
  return (
    <Box className={classes.centerPanel ?? ""}>
      <Box className={classes.chatHeader ?? ""}>
        <Avatar color="violet">{getInitials(activeConversation?.name)}</Avatar>
        <div style={{ flex: 1 }}>
          <Text weight={700}>
            {activeConversation?.name ?? "No Conversation"}
          </Text>
          <Text size="xs" color="teal">
            {online ? "Online " : "Offline"}
          </Text>
        </div>
        <Group spacing={6}>
          <ActionIcon variant="transparent">
            <IconDots size={18} />
          </ActionIcon>
          {/* <ActionIcon variant="transparent">
            <Avatar size={28} radius="sm">
              <Phone />
            </Avatar>
          </ActionIcon>
          <ActionIcon variant="transparent">
            <Avatar size={28} radius="sm">
              <Phone />
            </Avatar>
          </ActionIcon> */}
        </Group>
      </Box>

      <ScrollArea
        // ref={endRef}
        // style={{ backgroundColor: "#f8f9fa" }}
        className={classes.messagesWrap ?? ""}
        type="auto"
      >
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          {localMessages.map((m) => (
            <div
              key={m.id}
              className={classes.messageRow ?? ""}
              style={m.from === "me" ? { marginLeft: "auto" } : {}}
            >
              {m.from === "them" && (
                <Text size="xs" weight={600} style={{ marginBottom: 6 }}>
                  {m.senderName ?? activeConversation?.name ?? "Unknown"}
                </Text>
              )}

              <Paper
                className={
                  m.from === "me" ? classes.outgoing : classes.incoming
                }
                radius="md"
                shadow="xs"
              >
                <Text
                  size="sm"
                  sx={{
                    display: "inline-block",
                    maxWidth: "100%",
                    minWidth: 40,

                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "pre-wrap",
                  }}
                  style={
                    m.from === "me"
                      ? { color: "#fff", whiteSpace: "pre-wrap" }
                      : { whiteSpace: "pre-wrap" }
                  }
                >
                  {m.text}
                </Text>
                <Text
                  size="xs"
                  color={m.from === "me" ? "white" : "dimmed"}
                  style={{ marginTop: 8 }}
                >
                  {m.time}
                </Text>
              </Paper>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      </ScrollArea>
      {typing && (
        <div className={classes.bottomInput ?? ""}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text size="sm" color="dimmed" style={{ fontWeight: 600 }}>
              {typingUser ? `Typing…` : "Typing…"}
            </Text>

            <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: 18, opacity: 0.9 }}>•</span>
              <span style={{ fontSize: 18, opacity: 0.6, marginLeft: 6 }}>
                •
              </span>
              <span style={{ fontSize: 18, opacity: 0.3, marginLeft: 6 }}>
                •
              </span>
            </div>
          </div>
        </div>
      )}
      <div className={classes.bottomInput ?? ""}>
        <TextInput
          placeholder="Type a message..."
          icon={<IconSearch size={16} />}
          style={{ flex: 1 }}
          value={text}
          onChange={(e) => {
            setText(e.currentTarget.value);
            try {
              if (emitTyping && activeConversation?.otherUserId) {
                emitTyping({ recipientId: activeConversation.otherUserId });
              }
            } catch (e) {}
          }}
          // onKeyDown={handleKeyDown}
        />
        <ActionIcon size="lg" variant="filled">
          <IconPaperclip size={18} />
        </ActionIcon>

        <Button
          type="button"
          radius="xl"
          leftIcon={<IconSend size={16} />}
          onClick={(e: any) => {
            e.preventDefault();
            handleSend();
          }}
          disabled={text.trim() === "" || !activeConversation}
        >
          Send
        </Button>
      </div>
    </Box>
  );
}

function getInitials(name?: string) {
  if (!name) return "NA";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
